/**
 * What a map membership actually adds, in one place so every upgrade prompt
 * says the same thing. Keep this to what the code enforces:
 * - Environmental samples: DAILY_LIMIT a day for free identities, enforced
 *   server-side (`api/_lib/envUsageQuota.js`).
 * - Dispersed camps: free callers get coordinates jittered ~1 mile and no
 *   notes (`api/geo/dispersed-camps.js`).
 * Every map layer, tapping into any spot, and saved spots are free nationwide.
 * Home state is an identifier (map default, local species), never a limit.
 */
export const MEMBERSHIP_PATH = "/membership";

export const MEMBERSHIP_PLAN_NAME = "Annual membership";

/** What the annual plan adds. Keep aligned with the gates named above. */
export const MEMBERSHIP_PLAN_POINTS = [
  "Unlimited environmental samples",
  "Exact dispersed camp locations, with notes",
  "A lower fee when hosting experiences",
] as const;

export const MEMBERSHIP_PITCH_TITLE = "Go further with a membership.";

export const MEMBERSHIP_PITCH_BODY =
  "Unlimited environmental samples and exact dispersed camp locations with notes. Members also pay a lower fee when hosting experiences.";

export const SIGNED_OUT_BANNER_BODY =
  "Create a free account to remember foraging locations and unlock environmental readings. Members get unlimited samples and exact camp locations.";

export type MembershipFaqFacts = {
  /** e.g. "5"; null when the plan API did not report it. */
  freeDailySamples: number | null;
};

/**
 * /membership FAQ. Numbers come from the plan API, so each answer still reads
 * correctly when one is missing.
 *
 * Funding claims are the owner's (2026-10-02): membership pays for the nature
 * data and models, and there are no ads or investors. "Never sell personal
 * information" matches the Privacy Policy. Don't add a donation %, a named
 * research partner, or open-data sharing until those are true.
 */
export function buildMembershipFaq({
  freeDailySamples,
}: MembershipFaqFacts): { q: string; a: string }[] {
  const samples = freeDailySamples != null ? String(freeDailySamples) : "a few";
  return [
    {
      q: "Where does my membership go?",
      a: "Into the data and models behind Wild Network: satellite imagery for the fall color map, soil and rain data for every reading on the map, species records for the field guide, and the forecasts we build from all of it. Some of that data is paid for every time it's read.",
    },
    {
      q: "Why is Wild Network funded by members?",
      a: "Wild Network has no ads and no investors, and we never sell your personal information. Members pay for it directly.",
    },
    {
      q: "What do members get?",
      // Fee numbers live only in the page's comparison table.
      a: "Unlimited environmental samples, exact dispersed camp locations with notes, a lower fee when you host, and an Annual member badge on your host profile.",
    },
    {
      q: "What stays free?",
      a: `Every map layer, tapping any spot, saved spots nationwide, and the field guide are free. Free accounts also get ${samples} environmental samples a day and see dispersed camps to within about a mile.`,
    },
    {
      q: "Can I cancel?",
      a: "Yes, any time, from Manage membership on this page or in Settings. Cancelling stops the next renewal; it does not delete your account or your saved spots.",
    },
    {
      q: "Does it work in the mobile app?",
      a: "Yes. Membership belongs to your Wild Network account, so it works on the web and in the app when you sign in with the same account. If you joined through the App Store, manage it in your Apple subscriptions.",
    },
    {
      q: "Who handles payment?",
      a: "Checkout runs on Stripe. Your card details go to Stripe, not to Wild Network. Membership renews automatically each year until you cancel.",
    },
  ];
}
